import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  AppInstallTeaser as AppInstallTeaserFixed,
  BasketBanner,
  BasketCoins,
  BasketCouponDeepLinkActivation,
  BasketFAQ,
  BasketHintSection,
  BasketInfo,
  BasketMOV,
  BasketMOVAccordion,
  BasketMeta,
  BasketPageCouponLoginChecker,
  BasketPageTitle,
  BasketRecommendations,
  BasketSplitView,
  BasketTotal,
  BasketTracker,
  ComplementaryRecommendation,
  CustomerService,
  DESKTOP_STICKY_HEIGHT,
  Deliveries,
  EmptyBasketSection,
  FreeDeliverySectionInfoMessage,
  FullHeightProductTile,
  PromotionBanner,
  RecommendationWrapper,
  SPACER_BEFORE_STICKY_HEADER,
  UnavailableProducts,
  useDiscounts,
  useIsComplementaryRecommendationEnabled,
  useIsNewBannerFrameworkActive,
} from '@aboutyou/logic-components';
import {
  COLORS,
  FullPageSpinner,
  ModalDialog,
  RowSection,
  SPACING,
  gridColsWidth,
  useLockBodyScroll,
} from '@aboutyou/ui-components';
import {useBasketCount} from '@aboutyou/user';
import {styled as linariaStyled} from '@linaria/react';
import React, {useEffect, useRef, useState} from 'react';
import {Sticky} from 'react-stickup-ay';
import styled, {css} from 'styled-components';
import {CheckoutPage} from '../CheckoutPage/CheckoutPage';

export const BasketPageEager = () => {
  const container = useRef<HTMLDivElement | null>(null);
  const {t} = useTranslation();
  const [isLoading, setLoading] = useState(false);

  useLockBodyScroll({lockScroll: isLoading});

  const showComplementaryRecommendations =
    useIsComplementaryRecommendationEnabled();

  const stickyStyleOverWrite = {width: '100%'};

  const [isMovReached, setIsMovReached] = useState(false);
  const onBasketQuantityChange = (props: {isMovReached?: boolean}) => {
    setFreeDeliverySection(undefined);
    setIsMovReached(Boolean(props.isMovReached));
  };

  const [freeDeliverySection, setFreeDeliverySection] =
    useState<FreeDeliverySection>();

  const onAddToBasketSuccess = ({
    freeDeliverySection,
  }: {
    freeDeliverySection?: FreeDeliverySection;
  }) => {
    setFreeDeliverySection(freeDeliverySection);
  };

  const onRemoveToBasketSuccess = () => {
    setFreeDeliverySection(undefined);
  };

  useEffect(() => {
    CheckoutPage.preload();
  }, []);

  const activateNewBannerFramework = useIsNewBannerFrameworkActive();

  // When the basket is empty the split view collapses to a single block, which
  // would drop the right-column PromotionBanner to the very bottom of the page.
  // In that state we render the banner at the top instead (see PromotionBanner placement below).
  const isBasketEmpty = useBasketCount() === 0;

  const discounts = useDiscounts();

  // useEffect is here to make sure we get latest discounts available.
  // after that refetch is resolved, it will update discount, validate if discount is still there and then update useUserSelectedDiscount
  // when useUserSelectedDiscount is update, it will trigger useBasketTotalSection again to fetch new prices and other informations
  useEffect(() => {
    discounts.refetch();
  }, []);

  return (
    <PageWrapper>
      {isLoading && <FullPageSpinner />}
      <BasketPageLayout>
        <BasketPageCouponLoginChecker withAboveHeader />
        <BasketCouponDeepLinkActivation LayoutComponent={ModalDialog} />
        <BasketMeta />
        <BasketTracker />
        <RowSection>
          <StyledBasketPageTitle />
        </RowSection>
        {activateNewBannerFramework && isBasketEmpty && (
          <RowSection>
            <PromotionBannerWrapper>
              <PromotionBanner
                data-testid="basketPromotionBanner"
                ContentPopperLayoutComponent={ModalDialog}
              />
            </PromotionBannerWrapper>
          </RowSection>
        )}
        <BasketSplitView
          ref={container}
          contentStart={
            <>
              <AppInstallTeaser
                buttonOptions={{
                  closeable: false,
                  showInformationButton: true,
                  showStoreButtons: true,
                }}
                title={t('CAMPAIGNS.APP_INSTALL.HEADLINE')}
              />
              {!activateNewBannerFramework && <BasketBanner containBanner />}
              <StyledFreeDeliverySectionInfoMessage
                infoMessage={
                  freeDeliverySection?.type?.$case === 'infoMessage'
                    ? freeDeliverySection.type.infoMessage
                    : undefined
                }
              />
              <StyledBasketMOV
                $isMovReached={isMovReached}
                ProgressMoreInfoLayoutComponent={ModalDialog}
                padded
                RecommendationsComponent={BasketMOVAccordion}
                onAddToBasketSuccess={onAddToBasketSuccess}
              />
              <Deliveries
                onBasketQuantityChange={onBasketQuantityChange}
                onBasketItemRemovedSuccessfully={onRemoveToBasketSuccess}
              />
              <StyledEmptySection resizeEnabled />
              <UnavailableProducts SizeReminderLayoutComponent={ModalDialog} />
              {showComplementaryRecommendations && (
                <RecommendationWrapper
                  ViewComponent={ComplementaryRecommendation}
                  addToBasketMethod="miniAdp"
                  addToCTALabel={t('Add')}
                  name="COMPLEMENTARY_RECOMMENDATION_MINI_ADP"
                  smallerPaddings={false}
                  showNavigation
                  onAddToBasketSuccess={onAddToBasketSuccess}
                  isSmallFormat
                  enableGridLayout={false}
                  ProductTileComponent={FullHeightProductTile}
                />
              )}
              <BasketFAQ />
              <CustomerService />
            </>
          }
          contentEnd={
            <>
              <Sticky
                container={container}
                experimentalNative
                overflowScroll="flow"
                style={stickyStyleOverWrite}
                defaultOffsetTop={
                  DESKTOP_STICKY_HEIGHT + SPACER_BEFORE_STICKY_HEADER
                }
              >
                <StyledBasketCoins LayoutComponent={ModalDialog} />
                <BasketInfo placementFilter={1} />

                <BasketTotal
                  withTileWrapper
                  withDesktopTeaser
                  withDiscountCard={!activateNewBannerFramework}
                  checkoutSource="desktop-basket-page"
                  onExpressPaymentStart={() => {
                    setLoading(true);
                  }}
                  onExpressPaymentEnd={() => {
                    setLoading(false);
                  }}
                />
                <BasketInfo placementFilter={2} />
                {activateNewBannerFramework && !isBasketEmpty && (
                  <PromotionBannerSlot>
                    <PromotionBanner
                      data-testid="basketPromotionBanner"
                      ContentPopperLayoutComponent={ModalDialog}
                    />
                  </PromotionBannerSlot>
                )}
              </Sticky>
            </>
          }
        />
        <RecommendationContainer>
          <RecommendationWrapper
            ViewComponent={BasketRecommendations}
            addToBasketMethod="miniAdp"
            onAddToBasketSuccess={onAddToBasketSuccess}
          />
        </RecommendationContainer>
      </BasketPageLayout>
      <BasketHintSection />
    </PageWrapper>
  );
};

const StyledFreeDeliverySectionInfoMessage = styled(
  FreeDeliverySectionInfoMessage,
)`
  margin-bottom: 16px;
`;

const StyledBasketMOV = styled(BasketMOV)<{$isMovReached: boolean}>`
  ${props =>
    props.$isMovReached
      ? css`
          margin-bottom: 16px;
        `
      : css`
          border-radius: 5px;
          box-shadow: 0 2px 4px 0 rgb(0 0 0 / 4%), 0 10px 15px 0 rgb(0 0 0 / 4%);
          border: solid 1px ${COLORS.Grey3};
          background-color: ${COLORS.White};
          padding: 24px;
          margin-bottom: 14px;
        `}
`;

const StyledBasketPageTitle = styled(BasketPageTitle)`
  margin-top: ${SPACING['Y.Lg']};

  &:empty {
    margin-top: ${SPACING['Y.None']};
  }
`;

const RecommendationContainer = styled.div`
  padding-bottom: 50px;
  padding-top: 20px;
`;

const PageWrapper = styled.div`
  position: relative;
`;

const StyledEmptySection = styled(EmptyBasketSection)`
  padding: 20px 0;
`;

const StyledBasketCoins = linariaStyled(BasketCoins)`
  margin-bottom: 0.75rem;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid ${COLORS.Grey3};
  background: ${COLORS.White};
  box-shadow: 0px 10px 15px 0px rgba(48, 33, 33, 0.04), 0px 2px 4px 0px rgba(0, 0, 0, 0.04);
`;

const BasketPageLayout = styled.div``;

const AppInstallTeaser = styled(AppInstallTeaserFixed)`
  &&& {
    position: relative;
    margin: 10px 0px 10px 0px;
    width: 100%;
    z-index: 0;
  }
`;

const PromotionBannerWrapper = styled.div`
  margin-top: ${SPACING['Y.Xl2']};
  margin-inline: auto;
  max-width: ${gridColsWidth(8)};

  &:empty {
    margin-top: ${SPACING['Y.None']};
  }
`;

const PromotionBannerSlot = styled.div`
  margin-bottom: 14px;

  &:empty {
    margin-bottom: ${SPACING['Y.None']};
  }
`;
